import type { StatusType } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

import { store } from 'reduxStore';
import { LOG_ERROR_CATEGORY, LOGGER_TYPE } from '../api/types';
import type { TOAST_LOGGER_TYPE } from '../hooks/useToast';

/**
 * Error source types for categorizing where the error originated
 */
export type ErrorSource =
  | 'error_boundary'
  | 'callback'
  | 'promise'
  | 'global'
  | 'api';

/**
 * Context for error logging to provide actionable information
 */
export interface ErrorContext {
  /** What action the user was performing (e.g., "addTopic", "saveFlow") */
  action?: string;
  /** Component or module where the error occurred */
  componentName?: string;
  /** Source of the error */
  source?: ErrorSource;
  /** React component stack (from error boundaries) */
  componentStack?: string | null;
  /** Additional context specific to the error */
  extra?: Record<string, unknown>;
}

/**
 * Builds standardized error log body with consistent fields
 */
function buildErrorLogBody(
  error: Error,
  context: ErrorContext = {},
  accountId?: string,
  projectId?: string,
): Record<string, unknown> {
  const user = store.getState().auth.user || {};
  const errorFingerprint = `${error.name}:${error.message}`.slice(0, 100);

  return {
    // IDs
    project_id: projectId || '*',
    account_id: accountId || '*',
    user_email: user.email || 'Unknown user',

    // Error details
    error_stack: error.stack,
    error_name: error.name,
    error_message: error.message,
    error_fingerprint: errorFingerprint,

    // Location
    url: window.location.href,
    pathname: window.location.pathname,

    // Context
    source: context.source,
    action: context.action,
    component_name: context.componentName,
    component_stack: context.componentStack,

    // Extra
    ...context.extra,
  };
}

/**
 * Logs an error to Datadog with standardized context.
 * Use this for manual error catching in callbacks, promises, etc.
 *
 * @example
 * ```ts
 * try {
 *   await riskyOperation();
 * } catch (error) {
 *   logError(error as Error, {
 *     action: 'saveFlow',
 *     componentName: 'FlowEditor',
 *     source: 'callback',
 *   }, accountId, projectId);
 * }
 * ```
 */
export function logError(
  error: Error,
  context: ErrorContext = {},
  accountId?: string,
  projectId?: string,
): void {
  const logBody = buildErrorLogBody(error, context, accountId, projectId);

  // Always log to Datadog Logs (works even without user consent)
  logToDataDog(
    LOGGER_TYPE.REACT_ERROR,
    'error',
    `[${error.name}] ${error.message}`,
    logBody,
    accountId,
    projectId,
  );

  // Also send to Datadog RUM for unminified stack traces and session replay
  // RUM may not be initialized if user denied consent - this is safe to call anyway
  try {
    datadogRum.addError(error, {
      source: context.source,
      action: context.action,
      component_name: context.componentName,
      account_id: accountId,
      project_id: projectId,
    });
  } catch {
    // RUM not initialized - ignore silently
  }
}

/**
 * Sets up global error handlers to catch uncaught errors and unhandled promise rejections.
 * Call this once during app initialization.
 *
 * @example
 * ```ts
 * // In app/index.tsx, after Datadog init
 * setupGlobalErrorHandlers();
 * ```
 */
export function setupGlobalErrorHandlers(): void {
  // Catch uncaught errors (callbacks, setTimeout, etc.)
  window.addEventListener('error', event => {
    // Ignore errors without an Error object (e.g., script loading errors)
    if (!event.error) {
      return;
    }

    const error = event.error as Error;
    logError(error, {
      source: 'global',
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Catch unhandled promise rejections
  window.addEventListener('unhandledrejection', event => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    logError(error, {
      source: 'promise',
      extra: {
        type: 'unhandled_rejection',
        original_reason:
          event.reason instanceof Error ? undefined : String(event.reason),
      },
    });
  });
}

enum ErrorCategory {
  NORMAL = 20,
  USER = 40,
  SYSTEM = 50,
  UI = 55,
}

const checkE = (e: string, E: ErrorCategory) => e.startsWith(E.toString());

export const getErrorCategory = (errorID?: string, statusCode?: number) => {
  if (statusCode === 403) {
    return LOG_ERROR_CATEGORY.NORMAL;
  }
  if (statusCode === 404) {
    return LOG_ERROR_CATEGORY.UI;
  }
  if (!errorID) {
    return LOG_ERROR_CATEGORY.UNDEFINED;
  }

  if (checkE(errorID, ErrorCategory.NORMAL)) {
    return LOG_ERROR_CATEGORY.NORMAL;
  }
  if (checkE(errorID, ErrorCategory.USER)) {
    return LOG_ERROR_CATEGORY.USER;
  }
  if (checkE(errorID, ErrorCategory.SYSTEM)) {
    return LOG_ERROR_CATEGORY.SYSTEM;
  }
  if (checkE(errorID, ErrorCategory.UI)) {
    return LOG_ERROR_CATEGORY.UI;
  }

  return LOG_ERROR_CATEGORY.UNDEFINED;
};

export const logToDataDog = (
  loggerType: TOAST_LOGGER_TYPE | LOGGER_TYPE,
  statusType: StatusType,
  message: string,
  body: Record<string, any>,
  accountId?: string,
  projectId?: string,
) => {
  const ddLogger = datadogLogs.getLogger(loggerType);
  if (!!ddLogger) {
    // We always want to log these things
    const project_id = accountId || '*';
    const account_id = projectId || '*';
    const user = store.getState().auth.user || {};

    const logBody = {
      project_id,
      account_id,
      user_email: user.email || 'Unknown user',
      ...body,
    };

    switch (statusType) {
      case 'error':
        return ddLogger.error(message, logBody);
      case 'warn':
        return ddLogger.warn(message, logBody);
      case 'debug':
        return ddLogger.debug(message, logBody);
      case 'info':
      default:
        return ddLogger.info(message, logBody);
    }
  }
};

type ActionUserFlow =
  | 'Agent'
  | 'Account'
  | 'Alert'
  | 'Analytics'
  | 'Assistant'
  | 'Assistant Chat'
  | 'Assistants'
  | 'APIs'
  | 'Audio Management'
  | 'Billing'
  | 'Behaviour'
  | 'Config Builder'
  | 'Config Form'
  | 'Conversation Review'
  | 'Conversations'
  | 'Create Assistant'
  | 'Create Analysis'
  | 'CSAT'
  | 'Custom Metrics'
  | 'Analysis Dashboard'
  | 'Dashboards'
  | 'Draft'
  | 'Entity'
  | 'Environments'
  | 'Flow'
  | 'Flows'
  | 'Function'
  | 'Function Step'
  | 'Functions'
  | 'Handoffs'
  | 'Help'
  | 'History'
  | 'Integrations'
  | 'Knowledge'
  | 'Knowledge Base'
  | 'Model Configuration'
  | 'Model Training'
  | 'Phone Numbers'
  | 'Pronunciations'
  | 'Response Control'
  | 'Rules'
  | 'Secrets'
  | 'Settings'
  | 'Sites'
  | 'SMS'
  | 'Speech Recognition'
  | 'Start Function'
  | 'Stop Keywords'
  | 'Template Assistant'
  | 'Test Suite'
  | 'Test Case'
  | 'Test Cases'
  | 'Transcript Corrections'
  | 'Voice'
  | 'Deploy Voice'
  | 'Variant Management'
  | 'Support'
  | 'Language Hub'
  | 'Source Hub';

type ActionModifier =
  | 'click'
  | 'select'
  | 'open'
  | 'close'
  | 'cancel'
  | 'edit'
  | 'reset'
  | 'create'
  | 'update'
  | 'delete'
  | 'publish'
  | 'use'
  | 'copy'
  | 'duplicate'
  | 'download'
  | 'export';

// used if an action isn't contained to one log
type ActionState = 'start' | 'complete';

export const getActionName = (
  userFlow: ActionUserFlow,
  type?: ActionModifier,
  subject?: string,
  state?: ActionState,
) => {
  const actionContext = [state, type, subject].filter(item => !!item).join(' ');

  if (actionContext.length === 0) {
    return userFlow;
  }
  return `${userFlow}: ${actionContext}`;
};
